import React, { useMemo, useState } from 'react';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Checkbox,
  TableHead,
  IconButton
} from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';
import { get, isSet } from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import clsx from 'clsx';

import { LineItem } from '../Types';
import { BundleCountSelection } from './BundleCountSelection';
import {
  getBundlePackDollarAmount,
  isMedical,
  isPackLineItem,
  isRefunded,
  noRefundAvailable
} from './utils';
import { EditLineItemAmount } from './EditLineItemAmount';
import OnOffToggle from '@/components/OnOffToggle';

const useStyles: any = makeStyles((theme: any) => ({
  root: {
    '& .MuiTableRow-root': {
      border: 'none',
      '& span:hover': {
        backgroundColor: 'transparent !important'
      },
      '& td:first-child': {
        marginLeft: -9
      },
      '& td:last-child': {
        minWidth: 80,
        '& > div': {
          paddingRight: 56
        }
      },
      '& th[title]': {
        cursor: 'pointer'
      }
    }
  },
  tableContainer: {
    minHeight: 75
  },
  headerCheckbox: {
    padding: '4px 10px 6px 0',
    height: 24
  },
  centerAlign: {
    textAlign: 'center'
  },
  editButton: {
    paddingTop: 0,
    height: 32,
    marginLeft: 6
  },
  editIconEnabled: {
    color: theme.palette.primary[600],
    paddingTop: 0
  },
  lineItemRowDisabled: {
    cursor: 'default',
    '& td > *': {
      opacity: 0.5
    },
    '& *': {
      cursor: 'default'
    },
    '& span': {
      backgroundColor: 'white'
    },
    '& .MuiInputBase-root': {
      display: 'none'
    }
  },
  table: {
    borderCollapse: 'collapse',
    '& tbody tr': {
      borderBottom: '1px solid #D1D5DB !important'
    },
    '& tbody': {
      border: 'none !important'
    }
  },
  tableHeadingCell: {
    color: '#000 !important',
    padding: '6px 0 6px 15px !important',
    borderBottom: 'none !important',
    height: 16,
    lineHeight: '24px',
    backgroundColor: '#E6F0F9 !important',
    fontWeight: '600 !important',
    whiteSpace: 'nowrap'
  },
  tableCell: {
    color: '#000 !important',
    padding: '8px 0 6px 15px !important',
    height: 32,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: '-webkit-baseline-middle'
  },
  checkCell: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    paddingRight: 0,
    maxWidth: 262
  },
  creditCell: {
    maxWidth: 94
  }
}));

const StyledCheckbox = withStyles({
  root: {
    paddingTop: 6,
    paddingLeft: 0,
    height: 30
  }
})(Checkbox);

export type TableHeaderInfo = {
  id: string;
  label: string;
  selectAll: boolean;
  title?: string;
};

const MEDICAL_TABLE_HEAD: TableHeaderInfo[] = [
  {
    id: 'description',
    label: 'Medical Items',
    selectAll: true
  },
  {
    id: 'list_price',
    label: 'Price',
    selectAll: false
  },
  {
    id: 'manual_discount',
    label: 'Discount',
    selectAll: false
  },
  {
    id: 'credits_used',
    label: 'Credits Used',
    selectAll: false
  },
  {
    id: 'final_amount',
    label: 'Total',
    selectAll: false,
    title: 'Refund with tax'
  },
  {
    id: 'service_administered',
    label: 'Service Administered',
    selectAll: false,
    title: 'Did the client receive this medical service?'
  }
];

const NON_MEDICAL_TABLE_HEAD: TableHeaderInfo[] = [
  {
    id: 'description',
    label: 'Items',
    selectAll: true
  },
  {
    id: 'list_price',
    label: 'Price',
    selectAll: false
  },
  {
    id: 'manual_discount',
    label: 'Discount',
    selectAll: false
  },
  {
    id: 'credits_used',
    label: 'Credits Used',
    selectAll: false
  },
  {
    id: 'final_amount',
    label: 'Total',
    selectAll: false,
    title: 'Refund with tax'
  },
  {
    id: 'service_administered',
    label: '',
    selectAll: false
  }
];

const bundle_count_col = {
  id: 'bundle_count',
  label: 'Bundle Items',
  selectAll: false
};

const blank_col = {
  id: 'bundle_count',
  label: '',
  selectAll: false
};

const priceDisplay = (
  row: LineItem,
  valuePath: string,
  replaceWithIfZero: string | boolean
) => {
  const v = get(row, valuePath, replaceWithIfZero);
  const d =
    ((typeof v === 'number' && v === 0) || !v) && replaceWithIfZero
      ? replaceWithIfZero
      : '$' + parseFloat(v).toFixed(2);
  return d;
};

const creditDisplay = (
  row: LineItem,
  valuePath: string,
  replaceWithIfZero: string | undefined
) => {
  const v = get(row, valuePath, replaceWithIfZero);
  return ((typeof v === 'number' && v === 0) || !v) &&
    replaceWithIfZero !== undefined
    ? replaceWithIfZero
    : parseInt(v ?? '0');
};

const finalAmountDisplay = (row: LineItem) => {
  const valuePath =
    row.edit_amount === undefined || !row.selected
      ? 'refund_amount'
      : 'edit_amount';
  return row.credits_used
    ? creditDisplay(row, valuePath, ' -- ')
    : priceDisplay(row, valuePath, ' -- ');
};

interface LineItemSelectionProps {
  purchaseLineItems: LineItem[];
  selectableItemCount: number;
  onLineItemSelectionChange: (
    row: LineItem | undefined,
    checked?: boolean,
    checkAll?: boolean
  ) => void;
  onBundleCountChange: (row: LineItem, count: number) => void;
  onLineItemAmountChange: (row: LineItem, amount: number) => void;
  onLineItemServiceGivenChange: (row: LineItem, checked: boolean) => void;
}

export const LineItemSelection: React.FC<LineItemSelectionProps> = ({
  purchaseLineItems,
  selectableItemCount,
  onLineItemSelectionChange,
  onBundleCountChange,
  onLineItemAmountChange,
  onLineItemServiceGivenChange
}) => {
  const classes = useStyles();
  const [allSelected, setAllSelected] = useState(false);
  const [showBundleItemsCol, setShowBundleItemsCol] = useState(false);
  const [lineItemForEdit, setLineItemForEdit] = useState<LineItem | undefined>(
    undefined
  );
  const [editLineItemAmountOpen, setEditLineItemAmountOpen] = useState(false);

  const selection = useMemo(
    () => purchaseLineItems.filter(i => i.selected),
    [purchaseLineItems]
  );

  const itemsWithSelectionOnlyAmounts = useMemo(() => {
    // if more than 0 items and all are selected check the select all cb
    setAllSelected(
      selection.length === selectableItemCount && selectableItemCount !== 0
    );
    const items = purchaseLineItems.map(i => {
      // refund_amount is display only
      let refund_amount = 0;
      const isSelected = selection.some(s => s.id === i.id);
      if (isSelected) {
        // use edit_amount in the case the user has edited the line item refund amt
        if (i.edit_amount === undefined) {
          if (isPackLineItem(i)) {
            refund_amount = getBundlePackDollarAmount(i);
          } else {
            refund_amount = i.final_amount || i.credits_used;
          }
        } else {
          refund_amount = i.edit_amount;
        }
      }
      return {
        ...i,
        refund_amount: refund_amount
      };
    });

    return {
      medical: items.filter(i => i.is_medical),
      nonMedical: items.filter(i => !i.is_medical || isSet(i.is_medical))
    };
  }, [selection]);

  const medical_table_columns = useMemo(() => {
    // if any line items are bundles we show a bundle selection column
    const showBundleCount = purchaseLineItems
      .filter(i => isMedical(i))
      .some(i => isPackLineItem(i));
    setShowBundleItemsCol(showBundleCount);
    if (showBundleCount) {
      return [
        ...MEDICAL_TABLE_HEAD.slice(0, 1),
        bundle_count_col,
        ...MEDICAL_TABLE_HEAD.slice(1, 6)
      ];
    } else {
      return [
        ...MEDICAL_TABLE_HEAD.slice(0, 1),
        blank_col,
        ...MEDICAL_TABLE_HEAD.slice(1, 6)
      ];
    }
  }, [purchaseLineItems]);

  const non_medical_table_columns = useMemo(() => {
    // if any line items are bundles we show a bundle selection column
    const showBundleCount = purchaseLineItems
      .filter(i => !isMedical(i))
      .some(i => isPackLineItem(i));
    setShowBundleItemsCol(showBundleCount);
    if (showBundleCount) {
      return [
        ...NON_MEDICAL_TABLE_HEAD.slice(0, 1),
        bundle_count_col,
        ...NON_MEDICAL_TABLE_HEAD.slice(1, 6)
      ];
    } else {
      return [
        ...NON_MEDICAL_TABLE_HEAD.slice(0, 1),
        blank_col,
        ...NON_MEDICAL_TABLE_HEAD.slice(1, 6)
      ];
    }
  }, [purchaseLineItems]);

  const editAmount = (row: LineItem) => {
    setLineItemForEdit(row);
    setEditLineItemAmountOpen(true);
  };

  const selectAll = (v: boolean) => {
    setAllSelected(v);
    onLineItemSelectionChange(undefined, undefined, v);
  };

  return (
    <div className={classes.root}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadingCell}>
              <Checkbox
                className={classes.headerCheckbox}
                onChange={() => selectAll(!allSelected)}
                checked={allSelected}
                key={String(allSelected)}
              />
              Refund Items: {selection.length}
            </TableCell>
            <TableCell colSpan={6} className={classes.tableHeadingCell} />
          </TableRow>
        </TableHead>
      </Table>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            {itemsWithSelectionOnlyAmounts.medical.length > 0 && (
              <TableRow>
                {medical_table_columns?.map((cell: TableHeaderInfo) => (
                  <TableCell
                    title={cell.title}
                    className={classes.tableHeadingCell}
                    key={cell.id}
                  >
                    {cell.label}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          {itemsWithSelectionOnlyAmounts.medical.length > 0 && (
            <TableBody test-id="medical-items">
              {itemsWithSelectionOnlyAmounts.medical.map((row: LineItem) => {
                const checked = selection.some(s => s.id === row.id);
                return (
                  <TableRow
                    key={row.id}
                    title={noRefundAvailable(row) ? row.unavailable_reason : ''}
                    className={
                      isRefunded(row) || noRefundAvailable(row)
                        ? classes.lineItemRowDisabled
                        : {}
                    }
                  >
                    <TableCell
                      className={clsx(classes.tableCell, classes.checkCell)}
                      title={row.description}
                    >
                      <StyledCheckbox
                        classes={classes.checkbox}
                        onChange={() =>
                          onLineItemSelectionChange(row, !checked)
                        }
                        checked={checked}
                        disabled={isRefunded(row) || noRefundAvailable(row)}
                      />
                      {<span>{row.description}</span>}
                    </TableCell>
                    <TableCell
                      className={clsx(classes.tableCell, classes.ellipsis)}
                    >
                      {showBundleItemsCol && isPackLineItem(row) ? (
                        <BundleCountSelection
                          onChange={e =>
                            onBundleCountChange(row, parseInt(e.target.value))
                          }
                          value={row.count_to_refund || 0}
                          maxAvailable={row.unused_bundle_count || 0}
                          disabled={!checked}
                        />
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span>{priceDisplay(row, 'list_price', false)}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span>
                        {priceDisplay(row, 'manual_discount', '0.00')}
                      </span>
                    </TableCell>
                    <TableCell
                      className={clsx(classes.tableCell, classes.creditCell)}
                    >
                      <span>
                        {row.credits_used
                          ? `${row.credits_used} ${row.credit_description}`
                          : '---'}
                      </span>
                    </TableCell>
                    <TableCell
                      className={classes.tableCell}
                      style={{ minWidth: 90 }}
                    >
                      <span>{finalAmountDisplay(row)}</span>
                      {!row.credits_used && (
                        <IconButton
                          size="small"
                          onClick={() => editAmount(row)}
                          title="Edit Refund Amount"
                          className={classes.editButton}
                          style={{ visibility: checked ? 'visible' : 'hidden' }}
                        >
                          <EditIcon
                            className={classes.editIconEnabled}
                            fontSize="small"
                          />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell
                      className={clsx(classes.tableCell, classes.centerAlign)}
                    >
                      {!(isRefunded(row) || noRefundAvailable(row)) && (
                        <OnOffToggle
                          isOn={row.service_administered ?? false}
                          onChange={isOn =>
                            onLineItemServiceGivenChange(row, isOn)
                          }
                          disabled={
                            !checked ||
                            isRefunded(row) ||
                            noRefundAvailable(row)
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          )}
          {itemsWithSelectionOnlyAmounts.nonMedical.length > 0 && (
            <>
              <TableHead>
                <TableRow>
                  {non_medical_table_columns?.map((cell: TableHeaderInfo) => (
                    <TableCell
                      key={cell.id}
                      title={cell.title}
                      className={clsx(
                        cell.id === 'service_administered'
                          ? classes.centerAlign
                          : '',
                        classes.tableHeadingCell
                      )}
                    >
                      {cell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody test-id="non-medical-items">
                {itemsWithSelectionOnlyAmounts.nonMedical.map(
                  (row: LineItem) => {
                    const checked = selection.some(s => s.id === row.id);
                    return (
                      <TableRow
                        key={row.id}
                        title={
                          noRefundAvailable(row) ? row.unavailable_reason : ''
                        }
                        className={
                          isRefunded(row) || noRefundAvailable(row)
                            ? classes.lineItemRowDisabled
                            : {}
                        }
                      >
                        <TableCell
                          className={clsx(classes.tableCell, classes.checkCell)}
                          title={row.description}
                        >
                          <StyledCheckbox
                            className={classes.checkbox}
                            onChange={() =>
                              onLineItemSelectionChange(row, !checked)
                            }
                            checked={checked}
                            disabled={isRefunded(row) || noRefundAvailable(row)}
                          />
                          {<span>{row.description}</span>}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {showBundleItemsCol && isPackLineItem(row) ? (
                            <BundleCountSelection
                              onChange={e =>
                                onBundleCountChange(
                                  row,
                                  parseInt(e.target.value)
                                )
                              }
                              value={row.count_to_refund || 0}
                              maxAvailable={row.unused_bundle_count || 0}
                              disabled={!checked}
                            />
                          ) : (
                            <span>&nbsp;</span>
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <span>{priceDisplay(row, 'list_price', false)}</span>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <span>
                            {priceDisplay(row, 'manual_discount', '0.00')}
                          </span>
                        </TableCell>
                        <TableCell
                          className={clsx(
                            classes.tableCell,
                            classes.creditCell
                          )}
                          style={{ minWidth: 90 }}
                        >
                          <span>
                            {row.credits_used
                              ? `${row.credits_used} ${row.credit_description}`
                              : '---'}
                          </span>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <span>{finalAmountDisplay(row)}</span>
                          {!row.credits_used && (
                            <IconButton
                              size="small"
                              onClick={() => editAmount(row)}
                              title="Edit Refund Amount"
                              className={classes.editButton}
                              style={{
                                visibility: checked ? 'visible' : 'hidden'
                              }}
                            >
                              <EditIcon
                                className={classes.editIconEnabled}
                                fontSize="small"
                              />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell
                          className={clsx(
                            classes.tableCell,
                            classes.centerAlign
                          )}
                        >
                          <div>--</div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>
      {editLineItemAmountOpen && (
        <EditLineItemAmount
          lineItem={lineItemForEdit}
          handleClose={() => setEditLineItemAmountOpen(false)}
          open={editLineItemAmountOpen}
          updateLineItemAmount={onLineItemAmountChange}
        />
      )}
    </div>
  );
};
